import {
  dimUnplayedBars,
  formatAudioDuration,
} from "@/lib/hooks/use-audio-waveform";
import { cn } from "@/lib/utils";
import { colorsV2 } from "@somethings-ui/core";
import { Check, Mic, Pause, Play, Trash2 } from "lucide-react";

export type RecorderUiState =
  | { kind: "default" }
  | { kind: "starting" }
  | { kind: "permission_denied" }
  | { kind: "unsupported" }
  | { kind: "recording"; durationMs: number; bars: number[] }
  | {
      kind: "uploading";
      durationMs: number;
      stage: "transcoding" | "uploading" | "saving";
    }
  | {
      kind: "completed";
      durationMs: number;
      bars: number[] | null;
      isPlaying: boolean;
      progressMs: number;
    };

interface RowAudioRecorderProps {
  state: RecorderUiState;
  otherRowRecording: boolean;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onPlay: () => void;
  onPause: () => void;
  onDelete: () => void;
  onReRecord: () => void;
  onRetryPermission: () => void;
}

const Bars = ({
  bars,
  height = 28,
  fill = colorsV2.brand.tangelo,
}: {
  bars: number[];
  height?: number;
  fill?: string;
}) => (
  <div
    className="flex flex-1 items-end gap-[3px]"
    style={{ height }}
    aria-hidden
  >
    {bars.map((v, i) => (
      <div
        key={i}
        style={{
          width: 2,
          height: `${Math.max(3, v * height)}px`,
          background: fill,
          borderRadius: 1,
        }}
      />
    ))}
  </div>
);

const InlineDefault = ({
  disabled,
  onMicTap,
}: {
  disabled: boolean;
  onMicTap: () => void;
}) => (
  <button
    type="button"
    onClick={onMicTap}
    disabled={disabled}
    aria-label="Record your answer"
    className={cn(
      "rounded-md p-1.5 hover:bg-zinc-100",
      disabled && "cursor-not-allowed opacity-40",
    )}
    style={{ color: colorsV2.text.secondary }}
  >
    <Mic className="h-4 w-4" />
  </button>
);

const PermissionDeniedState = ({ onRetry }: { onRetry: () => void }) => (
  <div
    className="mt-2 flex flex-col gap-2 self-stretch rounded-xl border border-[#fecaca] p-3"
    style={{ background: colorsV2.states.errorHighlight }}
    role="alert"
  >
    <p className="text-sm" style={{ color: colorsV2.states.error }}>
      Microphone access is blocked. Allow it in your browser settings.
    </p>
    <button
      type="button"
      onClick={onRetry}
      className="self-start rounded-md border border-[#fecaca] bg-white px-3 py-1 text-xs font-medium hover:bg-zinc-50"
      style={{ color: colorsV2.states.error }}
    >
      Try again
    </button>
  </div>
);

const UnsupportedState = () => (
  <div
    className="mt-2 rounded-xl border border-[#fecaca] p-3 text-sm"
    style={{
      background: colorsV2.states.errorHighlight,
      color: colorsV2.states.error,
    }}
    role="alert"
  >
    Voice recording isn&apos;t supported in this browser.
  </div>
);

const StartingRow = () => (
  <div className="mt-3 flex items-center gap-3 self-stretch">
    <div
      className="h-1.5 flex-1 overflow-hidden rounded-full"
      style={{ background: colorsV2.surfaces.z4 }}
    >
      <div
        className="h-full animate-pulse rounded-full"
        style={{ width: "30%", background: colorsV2.brand.tangelo }}
      />
    </div>
    <span className="text-xs" style={{ color: colorsV2.text.tertiary }}>
      Listening for your mic…
    </span>
  </div>
);

const RecordingRow = ({
  durationMs,
  bars,
  onStop,
}: {
  durationMs: number;
  bars: number[];
  onStop: () => void;
}) => (
  <div className="mt-3 flex items-center gap-3 self-stretch">
    <Bars bars={bars} height={28} />
    <span
      className="flex items-center gap-1.5 font-mono text-xs tabular-nums"
      style={{ color: colorsV2.brand.tangelo }}
    >
      <span
        className="inline-block h-1.5 w-1.5 animate-pulse rounded-full"
        style={{ background: colorsV2.brand.tangelo }}
      />
      {formatAudioDuration(durationMs)}
    </span>
    <button
      type="button"
      onClick={onStop}
      aria-label="Stop recording"
      className="ml-auto inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium hover:bg-zinc-100"
      style={{ color: colorsV2.text.secondary }}
    >
      <Check className="h-4 w-4" />
      Done
    </button>
  </div>
);

const UploadingRow = ({
  stage,
}: {
  stage: "transcoding" | "uploading" | "saving";
}) => {
  const label =
    stage === "transcoding"
      ? "Encoding…"
      : stage === "uploading"
        ? "Uploading…"
        : "Saving…";
  return (
    <div className="mt-3 flex items-center gap-3 self-stretch">
      <div
        className="h-1.5 flex-1 overflow-hidden rounded-full"
        style={{ background: colorsV2.surfaces.z4 }}
      >
        <div
          className="h-full animate-pulse rounded-full"
          style={{ width: "60%", background: colorsV2.brand.trueBlack }}
        />
      </div>
      <span className="text-xs" style={{ color: colorsV2.text.tertiary }}>
        {label}
      </span>
    </div>
  );
};

const CompletedRow = ({
  durationMs,
  bars,
  isPlaying,
  progressMs,
  locked,
  onPlay,
  onPause,
  onReRecord,
  onDelete,
}: {
  durationMs: number;
  bars: number[] | null;
  isPlaying: boolean;
  progressMs: number;
  locked: boolean;
  onPlay: () => void;
  onPause: () => void;
  onReRecord: () => void;
  onDelete: () => void;
}) => {
  const playedBars = dimUnplayedBars(bars, durationMs, progressMs);
  const lockClass = locked ? "cursor-not-allowed opacity-40" : "";

  return (
    <div className="mt-3 flex items-center gap-3 self-stretch">
      <button
        type="button"
        onClick={isPlaying ? onPause : onPlay}
        aria-label={isPlaying ? "Pause playback" : "Play recording"}
        disabled={locked}
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white",
          lockClass,
        )}
        style={{ background: colorsV2.brand.trueBlack }}
      >
        {isPlaying ? (
          <Pause className="h-3.5 w-3.5" />
        ) : (
          <Play className="ml-0.5 h-3.5 w-3.5" />
        )}
      </button>
      {playedBars ? (
        <Bars bars={playedBars} height={28} />
      ) : (
        <div className="h-7 flex-1" />
      )}
      <span
        className="flex items-center gap-1.5 font-mono text-xs tabular-nums"
        style={{ color: colorsV2.text.secondary }}
      >
        <span
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={{ background: colorsV2.states.success }}
        />
        {formatAudioDuration(durationMs)}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onReRecord}
          disabled={locked}
          className={cn(
            "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs hover:bg-zinc-100",
            lockClass,
          )}
          style={{ color: colorsV2.text.secondary }}
        >
          <Mic className="h-3 w-3" />
          Re-record
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={locked}
          aria-label="Delete recording"
          className={cn(
            "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs hover:bg-zinc-100",
            lockClass,
          )}
          style={{ color: colorsV2.text.secondary }}
        >
          <Trash2 className="h-3 w-3" />
          Delete
        </button>
      </div>
    </div>
  );
};

export function RowAudioRecorder({
  state,
  otherRowRecording,
  onStartRecording,
  onStopRecording,
  onPlay,
  onPause,
  onDelete,
  onReRecord,
  onRetryPermission,
}: RowAudioRecorderProps) {
  switch (state.kind) {
    case "default":
      return (
        <InlineDefault
          disabled={otherRowRecording}
          onMicTap={onStartRecording}
        />
      );
    case "starting":
      return <StartingRow />;
    case "permission_denied":
      return <PermissionDeniedState onRetry={onRetryPermission} />;
    case "unsupported":
      return <UnsupportedState />;
    case "recording":
      return (
        <RecordingRow
          durationMs={state.durationMs}
          bars={state.bars}
          onStop={onStopRecording}
        />
      );
    case "uploading":
      return <UploadingRow stage={state.stage} />;
    case "completed":
      return (
        <CompletedRow
          durationMs={state.durationMs}
          bars={state.bars}
          isPlaying={state.isPlaying}
          progressMs={state.progressMs}
          locked={otherRowRecording}
          onPlay={onPlay}
          onPause={onPause}
          onReRecord={onReRecord}
          onDelete={onDelete}
        />
      );
  }
}

export const isExpandedState = (state: RecorderUiState): boolean =>
  state.kind === "starting" ||
  state.kind === "recording" ||
  state.kind === "uploading" ||
  state.kind === "completed";
